import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DirtyInvalidErrorStateMatcher } from '@y42/angular/forms';
import { connectState, InputObservable } from '@y42/angular/util';
import { CopyToClipboardComponent } from '@y42/core/copy-to-clipboard';
import { IconModule } from '@y42/core/icon';
import { ReactiveLifecycleHooksDirective } from '@y42/core/reactive-lifecycle-hooks';
import { BehaviorSubject, map, Observable, switchMap } from 'rxjs';
import { ErrorMessages } from '../../models/error-messages';
import { selectFirstErrorMessage } from '../../util/error-messages';

const INPUT_IMPORTS: Component['imports'] = [
  CommonModule,
  ReactiveFormsModule,
  MatFormFieldModule,
  MatInputModule,
  MatTooltipModule,
  IconModule,
  CopyToClipboardComponent,
];
const errorStateMatcher = new DirtyInvalidErrorStateMatcher();

@UntilDestroy()
@Directive()
export class _InputComponent<T = unknown> extends ReactiveLifecycleHooksDirective {
  @ViewChild(MatInput) matInput!: MatInput;

  @Input()
  @InputObservable()
  label?: string | TemplateRef<unknown> | null;
  private readonly label$!: Observable<string | TemplateRef<unknown> | null>;

  @Input() formCtrl!: FormControl<T | null | undefined>;
  @Input() type: 'text' | 'password' | 'number' | 'textarea' = 'text';
  @Input() hideErrors = false;
  @Input() errorMessages?: ErrorMessages;
  @Input() required = false;
  @Input() color?: MatFormField['color'];
  @Input() tooltipHint?: string | null;
  @Input() enableCopyToClipboard = false;
  @Input() compact?: boolean | null;

  // HTMLInputElement properties
  @Input() placeholder?: HTMLInputElement['placeholder'];
  @Input() autocomplete?: HTMLInputElement['autocomplete'];
  @Input() readOnly?: HTMLInputElement['readOnly'];
  @Input() tabIndex?: HTMLInputElement['tabIndex'];
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() input = new EventEmitter<Event>();
  // ---

  readonly errorStateMatcher = errorStateMatcher;

  private readonly aclTooltip$$ = new BehaviorSubject('');

  readonly state = connectState(this, {
    labelTemplate: this.label$.pipe(
      map((label) => label instanceof TemplateRef),
      map((isTemplatRef) => (isTemplatRef ? (this.label as TemplateRef<unknown>) : null)),
    ),
    errorMessage: this.ngAfterViewInit$.pipe(
      map(() => this.formCtrl),
      switchMap((ctrl) => selectFirstErrorMessage(ctrl, this.errorMessages)),
    ),
    aclTooltip: this.aclTooltip$$,
  });

  setAclTooltip(message: string) {
    this.aclTooltip$$.next(message);
  }

  focus() {
    this.matInput.focus();
  }
}

@Component({
  selector: 'y42-text-input[formCtrl]',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  standalone: true,
  imports: INPUT_IMPORTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextInputComponent extends _InputComponent<string> {
  @Input() override type: _InputComponent['type'] = 'text';
}

@Component({
  selector: 'y42-number-input[formCtrl]',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  standalone: true,
  imports: INPUT_IMPORTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberInputComponent extends _InputComponent<number> {
  override readonly type: _InputComponent['type'] = 'number';
}
@Component({
  selector: 'y42-textarea-input[formCtrl]',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  standalone: true,
  imports: INPUT_IMPORTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextareaInputComponent extends _InputComponent<string> {
  override readonly type: _InputComponent['type'] = 'textarea';
}
